import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEstablishmentStore } from '../../store/establishmentStore';
import { useThemeStore } from '../../store/themeStore';
import { publicEstablishmentAPI } from '../../services/publicEstablishmentService';
import { getEstablishmentSlugFromUrl } from '../../utils/slugHelper';

interface UseEstablishmentLoaderOptions {
  /**
   * Quando true:
   * - ignora o cache do Zustand/localStorage mesmo se o slug for o mesmo
   * - faz bypass do cache HTTP para garantir config mais recente
   *
   * Útil para telas como Login/ForgotPassword quando o usuário chega por fluxos
   * que podem ter config antiga em cache (ex: convite).
   */
  forceRefresh?: boolean;
}

export const useEstablishmentLoader = (options?: UseEstablishmentLoaderOptions) => {
  const [isCheckingEstablishment, setIsCheckingEstablishment] = useState(true);
  const navigate = useNavigate();
  const lastForceRefreshedSlugRef = useRef<string | null>(null);
  const {
    setEstablishment,
    setLoading: setEstablishmentLoading,
    clearEstablishment,
  } = useEstablishmentStore();
  const { setColors } = useThemeStore();

  useEffect(() => {
    const loadEstablishment = async () => {
      const slug = getEstablishmentSlugFromUrl();

      if (!slug) {
        setIsCheckingEstablishment(false);
        return;
      }

      const forceRefresh = options?.forceRefresh === true;
      const forceRefreshKey = slug ? `establishment-force-refresh:${slug}` : null;
      const alreadyForceRefreshedThisSlug =
        (slug &&
          typeof window !== 'undefined' &&
          window.sessionStorage?.getItem(forceRefreshKey!) === '1') ||
        lastForceRefreshedSlugRef.current === slug;

      try {
        setEstablishmentLoading(true);

        const shouldBypassCache = forceRefresh && !alreadyForceRefreshedThisSlug;
        const data = await publicEstablishmentAPI.getBySlug(slug, {
          bypassCache: shouldBypassCache,
        });
        setEstablishment(data, slug);
        lastForceRefreshedSlugRef.current = slug;
        if (shouldBypassCache && forceRefreshKey && typeof window !== 'undefined') {
          window.sessionStorage?.setItem(forceRefreshKey, '1');
        }

        if (data.primaryColor && data.secondaryColor) {
          setColors({
            primary: data.primaryColor,
            secondary: data.secondaryColor,
          });
        }
        setIsCheckingEstablishment(false);
      } catch (err) {
        const error = err as any;
        if (error.response?.status === 404) {
          clearEstablishment();
          setIsCheckingEstablishment(false);
          navigate('/establishment-not-found', { replace: true });
          return;
        }
        setIsCheckingEstablishment(false);
      } finally {
        setEstablishmentLoading(false);
      }
    };

    loadEstablishment();
  }, [
    navigate,
    setColors,
    setEstablishment,
    setEstablishmentLoading,
    clearEstablishment,
    options?.forceRefresh,
  ]);

  return { isCheckingEstablishment };
};
